/**
 * @engine/vfx - VFX Graph Asset
 *
 * Content pipeline integration for VFX graphs.
 * Defines content type and importer for .vfx files.
 */

import type {
  ContentTypeDefinition,
  ImporterDefinition,
  ImportContext,
  ImportResult,
} from "@engine/aid";
import { FineIndex } from "@engine/aid";
import { serializeGraph, deserializeGraph } from "@engine/graph";
import type { ContextInstance } from "@engine/graph";
import { VFXGraph } from "./VFXGraph";
import { vfxShaderId, vfxShaderPath, type VFXShaderFormat } from "../runtime/VFXShaderCache";
import { VFX_DOC_VERSION } from "./types";
import type { VFXGraphData, VFXDocument, VFXNodeInstance, VFXContextFlowEdge } from "./types";
import { vfxValidator } from "./VFXGraphValidator";
import { compileVFXGraph } from "./compiler/VFXCompiler";
import { registerBuiltinVFXNodes } from "./nodes";

// Preset builders live in ./presets/* (one file per preset) — re-exported here so
// the public graph barrel and existing `@engine/vfx` import paths stay unchanged.
export {
  createFireworkVFXGraph,
  createCosmicAuroraVFX,
  createElectricStormVFX,
  createMagicPortalVFX,
  createPhoenixRisingVFX,
  createWaterRippleGridVFX,
  createSingularityVFX,
  createBlackHoleVFX,
  createTornadoVFX,
  createForceFieldVFX,
  createSmokeVFX,
  createExplosion2DVFX,
} from "./presets";

// ============================================================================
// CONTENT TYPE
// ============================================================================


// ============================================================================
// IMPORTER
// ============================================================================

/**
 * VFX Graph importer parameters.
 */
export interface VFXImporterParams {
  /** Maximum particles */
  maxParticles: number;

  /** Pre-compile shaders on import */
  precompileShaders: boolean;

  /** Generate shader variants */
  shaderVariants: {
    alpha: boolean;
    additive: boolean;
    premultiplied: boolean;
  };

  /** Compute workgroup size */
  workgroupSize: 64 | 128 | 256;
}

/**
 * Default VFX importer parameters.
 */
export const DEFAULT_VFX_IMPORTER_PARAMS: VFXImporterParams = {
  maxParticles: 10000,
  precompileShaders: true,
  shaderVariants: {
    alpha: true,
    additive: true,
    premultiplied: false,
  },
  workgroupSize: 64,
};

/**
 * Import a VFX graph file.
 */
async function importVFXGraph(context: ImportContext): Promise<ImportResult> {
  try {
    // Parse JSON content
    const content =
      typeof context.content === "string"
        ? context.content
        : new TextDecoder().decode(context.content as ArrayBuffer);

    // The shader codegen needs the VFX node definitions registered, else init/update nodes emit NOTHING
    // → particles spawn with hardcoded defaults (broken/laggy). Register here (idempotent) so a headless
    // reindex always compiles real shaders (the registry used to be populated only when the editor opened).
    registerBuiltinVFXNodes();

    // Reconstruct graph from the versioned .vfx document (envelope; legacy-flat fallback inside).
    const graph = deserializeVFXGraph(content);

    const params = { ...DEFAULT_VFX_IMPORTER_PARAMS, ...context.params };

    // Use new compiler for validation and compilation (async: Slang→WGSL via
    // the injected compile service)
    const compileResult = await compileVFXGraph(graph, {
      workgroupSize: (params.workgroupSize as number) ?? 64,
    });

    // Check for errors
    if (!compileResult.success) {
      const errorMessages = compileResult.errors
        .map((e) => `[${e.code}] ${e.message}`)
        .join("; ");
      return {
        success: false,
        error: `VFX compilation failed: ${errorMessages}`,
      };
    }

    if (compileResult.systems.length === 0) {
      return {
        success: false,
        error: "VFX compilation produced no systems",
      };
    }

    // Extract dependencies (textures, gradients)
    const dependencies: string[] = [];
    for (const node of graph.getNodes()) {
      const textureValue = node.inputValues.texture as
        | { guid?: string }
        | undefined;
      if (textureValue?.guid) {
        dependencies.push(textureValue.guid);
      }
    }

    // Compiled bundle: shaders go to the content-addressed VFX shader cache (keyed by a
    // hash of the source, so identical shaders across systems/effects dedupe); the bundle
    // only references shader **ids** — no inline WGSL. Runtime + build resolve ids via the
    // shader cache (editor: on-disk `<id>.wgsl`; player: build-emitted `vfx-shaders.json`).
    const compiledBundle: CompiledVFXBundle = {
      version: 3,
      particleStruct: compileResult.particleStruct,
      systems: [],
    };

    const format: VFXShaderFormat = 'wgsl';
    const writtenShaders = new Set<string>();

    // Cache a stage shader once (by content id) and return its id.
    const cacheShader = async (source: string, stage: string, sys: { id: string; name: string }): Promise<string> => {
      const id = vfxShaderId(source, format);
      if (!writtenShaders.has(id)) {
        writtenShaders.add(id);
        const path = vfxShaderPath(id);
        await context.writeFile(path, source);
        await FineIndex.registerProducedEntry(context.guid, path, 'vfx-shader', {
          metadata: { shaderId: id, format, stage, systemId: sys.id, systemName: sys.name },
        });
      }
      return id;
    };

    for (const sys of compileResult.systems) {
      const initShaderId = await cacheShader(sys.initShader, 'init', sys);
      const updateShaderId = await cacheShader(sys.updateShader, 'update', sys);
      const vertexShaderId = await cacheShader(sys.vertexShader, 'vertex', sys);
      const fragmentShaderId = await cacheShader(sys.fragmentShader, 'fragment', sys);

      compiledBundle.systems.push({
        id: sys.id,
        name: sys.name,
        maxParticles: sys.maxParticles,
        initShaderId,
        updateShaderId,
        vertexShaderId,
        fragmentShaderId,
        shaderFormat: format,
        spawn: {
          mode: sys.spawn.mode,
          rate: sys.spawn.rate,
          burst: sys.spawn.burst,
        },
        usesTexture: sys.usesTexture,
        textureSlot: sys.textureSlot,
        emitsLight: sys.emitsLight,
        lightRenderQuad: sys.lightRenderQuad,
        isLit: sys.isLit,
        lightConfig: sys.lightConfig ? { ...sys.lightConfig } : undefined,
      });
    }

    // Write compiled bundle (shader-id references only) to cache
    const cachePath = `/.cache/vfx/${context.guid}.json`;
    await context.writeFile(cachePath, JSON.stringify(compiledBundle));
    await FineIndex.registerProducedEntry(context.guid, cachePath, 'vfx-compiled', {
      metadata: { systemCount: compileResult.systems.length },
    });

    return {
      success: true,
      dependencies,
      importedData: {
        // Store metadata only (shaders are in cache file)
        compiledPath: cachePath,
        systemCount: compileResult.systems.length,
        maxParticles:
          (params.maxParticles as number) ??
          graph.getCapacity(),
        warnings: compileResult.warnings,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: `Failed to import VFX graph: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

/**
 * Compiled VFX bundle structure (saved to cache)
 */
export interface CompiledVFXBundle {
  version: number;
  particleStruct: string;
  systems: CompiledVFXSystem[];
}

/**
 * Single compiled system in bundle
 */
export interface CompiledVFXSystem {
  id: string;
  name: string;
  maxParticles: number;
  /**
   * Content-addressed shader-cache ids (resolve via `@engine/vfx` VFXShaderCache).
   * Shaders are NOT stored inline in the bundle — they live in the shader cache.
   */
  initShaderId: string;
  updateShaderId: string;
  vertexShaderId: string;
  fragmentShaderId: string;
  /** Source language of the referenced shaders (WGSL today; Slang later). */
  shaderFormat: VFXShaderFormat;
  spawn: {
    mode: string;
    rate?: number;
    burst?: {
      count: number;
      delay: number;
      repeat: number;
      interval: number;
    };
  };
  usesTexture?: boolean;
  textureSlot?: number;
  emitsLight?: boolean;
  lightRenderQuad?: boolean;
  isLit?: boolean;
  lightConfig?: {
    radius: number;
    intensity: number;
    falloff: number;
    colorMode: number;
    overrideColor: readonly number[];
    intensityFromAlpha: boolean;
  };
}

/**
 * VFX Graph importer definition.
 */
export const VFX_IMPORTER: ImporterDefinition = {
  id: "vfx.default",
  name: "VFX Graph Importer",
  supportedContentTypes: ["vfx"],
  defaultParams: DEFAULT_VFX_IMPORTER_PARAMS as unknown as Record<
    string,
    unknown
  >,
  paramsSchema: {
    type: "object",
    properties: {
      maxParticles: {
        type: "number",
        description: "Maximum particle count (100-1000000)",
        default: 10000,
      },
      precompileShaders: {
        type: "boolean",
        description: "Pre-compile shaders during import",
        default: true,
      },
      workgroupSize: {
        type: "number",
        enum: [64, 128, 256],
        description: "Compute shader workgroup size",
        default: 64,
      },
    },
  },
  import: importVFXGraph,
};

// ============================================================================
// VFX GRAPH FILE FORMAT
// ============================================================================

/**
 * Create a new empty VFX graph file content.
 */
export function createEmptyVFXGraph(name: string = "Untitled"): VFXGraphData {
  const graph = new VFXGraph();
  const data = graph.toVFXData();
  return {
    ...data,
    name,
  };
}

/**
 * Serialize a VFX graph to the versioned `.vfx` document (VFXDocument) JSON.
 *
 * The base graph (nodes/edges/variables) goes through the canonical `@engine/graph`
 * serializer — compact `x`/`y` + `fn/fp` edge keys + validation on load — with
 * `contextId` carried via the `nodeExtras` hook. VFX graph-level data (contexts /
 * flow edges / per-context settings) rides alongside. Accepts a live `VFXGraph` or
 * flat `VFXGraphData` (e.g. a preset).
 */
export function toVFXDocument(graph: VFXGraph | VFXGraphData): VFXDocument {
  const g = graph instanceof VFXGraph ? graph : VFXGraph.createFromVFXData(graph);
  const serialized = serializeGraph(g.toData(), {
    nodeExtras: (n) => ((n as VFXNodeInstance).contextId ? { contextId: (n as VFXNodeInstance).contextId } : undefined),
  });
  return {
    version: VFX_DOC_VERSION,
    graph: serialized,
    contexts: g.contexts,
    contextFlowEdges: g.contextFlowEdges,
    contextSettings: g.getContextSettingsMap(),
  };
}

export function serializeVFXGraph(graph: VFXGraph | VFXGraphData): string {
  return JSON.stringify(toVFXDocument(graph), null, 2);
}

/**
 * Deserialize a `.vfx` document JSON into a `VFXGraph`. Accepts the versioned
 * envelope (new) and, as a fallback, a legacy flat `VFXGraphData` (tests / old files).
 */
export function deserializeVFXGraph(json: string): VFXGraph {
  const raw = JSON.parse(json) as Record<string, unknown>;

  if (raw && typeof raw === 'object' && 'version' in raw && 'graph' in raw && raw.graph) {
    const doc = raw as unknown as VFXDocument;
    const base = deserializeGraph(doc.graph); // BaseGraphData; contextId restored via node extras
    const data: VFXGraphData = {
      ...base,
      contexts: (doc.contexts ?? []) as ContextInstance[],
      contextFlowEdges: (doc.contextFlowEdges ?? []) as VFXContextFlowEdge[],
      contextSettings: doc.contextSettings ?? {},
    } as VFXGraphData;
    return VFXGraph.createFromVFXData(data);
  }

  // Legacy flat VFXGraphData
  return VFXGraph.createFromVFXData(raw as unknown as VFXGraphData);
}
